import { FunctionalComponent, createContext, h } from 'preact';
import { StateUpdater, useCallback, useContext, useMemo, useState } from 'preact/hooks';
import Usercentrics, { BaseCategory, BaseService, Category, Service } from '@usercentrics/cmp-browser-sdk';
import { VIEW } from '../../enums';

export type UIService = Service | BaseService;
export type UICategory = Category | BaseCategory;

export const areServicesFull = (value: UIService[]): value is Service[] => {
  return value.some((item) => (item as Service).dataDistribution != null);
};

export const areCategoriesFull = (value: UICategory[]): value is Category[] => {
  return value.some((cat) => {
    return areServicesFull(cat.services);
  });
};

export interface CategoryContextType {
  categories: UICategory[];
  getUpdatedCategories: (view: VIEW) => Promise<UICategory[]>;
  services: UIService[];
  setCategories: StateUpdater<UICategory[]>;
  selectedSubService: Service | null;
  setSelectedSubService: StateUpdater<Service | null>;
}

const initialValue = {
  categories: [] as UICategory[],
  getUpdatedCategories: () => Promise.resolve([]),
  selectedSubService: null,
  services: [],
  setCategories: () => null,
  setSelectedSubService: () => null,
};

const CategoryContext = createContext<CategoryContextType>(initialValue);

interface Props {
  UC: Usercentrics;
}

export const CategoryContextProvider: FunctionalComponent<Props> = ({ children, UC }) => {
  const [categories, setCategories] = useState(initialValue.categories);
  const [selectedSubService, setSelectedSubService] = useState<Service | null>(initialValue.selectedSubService);

  const services = useMemo(
    () =>
      categories.reduce((acc: UIService[], cur) => {
        if (!cur.services) {
          return acc;
        }

        return [...acc, ...cur.services];
      }, []),
    [categories],
  );

  const getUpdatedCategories = useCallback(
    async (view: VIEW): Promise<UICategory[]> => {
      if (view === VIEW.SECOND_LAYER || areCategoriesFull(categories)) {
        return UC.getCategoriesFullInfo();
      }

      return UC.getCategoriesBaseInfo();
    },
    [UC, categories],
  );

  return (
    <CategoryContext.Provider
      value={{ categories, getUpdatedCategories, selectedSubService, services, setCategories, setSelectedSubService }}
    >
      {children}
    </CategoryContext.Provider>
  );
};

export const useCategoryContext = (): CategoryContextType => {
  return useContext(CategoryContext);
};
